import React from 'react'
import { useGlobalState } from 'state'
import { IDs, RainStatus, StageAlertLevel, ForecastStages, PumpAlertLevel, SuijinStatus, isPrivateMode, ForecastPumps } from 'common/consts'
import { ObservatoryType, ObservatoryTypeName } from 'common/types'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormControlLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Paper, Stack, Typography } from '@mui/material'
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import HexagonIcon from '@mui/icons-material/Hexagon'
import maplibregl from 'maplibre-gl'
import * as utils from 'common/utils'
import SuijinPng from 'assets/suijin.png'
import { LAYERS } from 'common/layers'


const getRainSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path
      d="M24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4Z"
      fill="${color}"
      fill-rule="nonzero"
      opacity="1"
      stroke="#000b0b"
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
    />
  </svg>`
}

const getStageSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path
      d="M2 43L24.055 5L46 43L2 43Z"
      fill="${color}"
      fill-rule="nonzero"
      opacity="1"
      stroke="#000b0b"
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
    />
  </svg>`
}

const getStageExSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path
      d="M2 43L24.055 5L46 43L2 43Z"
      fill="${color}"
      fill-rule="nonzero"
      opacity="1"
      stroke="#ff9800"
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="6"
    />
  </svg>`
}

const getCameraSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" height="48px" viewBox="0 0 24 24" width="48px" fill="${color}">
    <path d="M6.54757 6.82018L17.4524 6.82018L17.4524 17.1798L6.54757 17.1798L6.54757 6.82018Z" fill="#ffffff" fill-rule="nonzero" opacity="1" stroke="none" />
    <circle cx="12" cy="12" r="3.2"/>
    <path d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"/>
  </svg>`
}

const getTideSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" enable-background="new 0 0 20 20" height="48px" viewBox="0 0 20 20" width="48px" fill="${color}">
  <g><rect fill="none" height="20" width="20"/></g><g><polygon points="6,3 2,10 6,17 14,17 18,10 14,3"/></g>
  </svg>`
}

const getPumpSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlns:vectornator="http://vectornator.io">
  <g>
  <path d="M2 43L24.055 5L46 43L2 43Z" fill="${color}" fill-rule="nonzero" opacity="1" stroke="#000b0b" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" />
  <path d="M25.238 21.1861L18.238 21.1861L18.238 39.1861L22.238 39.1861L22.238 33.1861L25.238 33.1861C28.548 33.1861 31.238 30.4961 31.238 27.1861C31.238 23.8761 28.548 21.1861 25.238 21.1861ZM25.438 29.1861L22.238 29.1861L22.238 25.1861L25.438 25.1861C26.538 25.1861 27.438 26.0861 27.438 27.1861C27.438 28.2861 26.538 29.1861 25.438 29.1861Z" fill="#616161" fill-rule="nonzero" opacity="1" stroke="none" vectornator:layerName="path 1"/>
  </g>
  </svg>`
}

const getPumpExSvg = (color: string): string => {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" xmlns:vectornator="http://vectornator.io">
  <g>
  <path d="M2 43L24.055 5L46 43L2 43Z" fill="${color}" fill-rule="nonzero" opacity="1" stroke="#ff9800" stroke-linecap="round" stroke-linejoin="round" stroke-width="4" />
  <path d="M25.238 21.1861L18.238 21.1861L18.238 39.1861L22.238 39.1861L22.238 33.1861L25.238 33.1861C28.548 33.1861 31.238 30.4961 31.238 27.1861C31.238 23.8761 28.548 21.1861 25.238 21.1861ZM25.438 29.1861L22.238 29.1861L22.238 25.1861L25.438 25.1861C26.538 25.1861 27.438 26.0861 27.438 27.1861C27.438 28.2861 26.538 29.1861 25.438 29.1861Z" fill="#616161" fill-rule="nonzero" opacity="1" stroke="none" vectornator:layerName="path 1"/>
  </g>
  </svg>`
}

const getSuijinSvg = (color: string): string => {
  const fontColor = color === SuijinStatus.FLOOD1.color ? '#000000' : '#ffffff'
  return `<svg height="48.0pt" stroke-miterlimit="10" style="fill-rule:nonzero;clip-rule:evenodd;stroke-linecap:round;stroke-linejoin:round;" version="1.1" viewBox="0 0 48 48" width="48.0pt" xml:space="preserve" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
  <defs/>
  <g opacity="1">
  <path d="M24 2C11.856 2 2 11.856 2 24C2 36.144 11.856 46 24 46C36.144 46 46 36.144 46 24C46 11.856 36.144 2 24 2Z" fill="${color}" fill-rule="nonzero" opacity="1" stroke="#393939" stroke-linecap="butt" stroke-linejoin="round" stroke-width="1"/>
  <path d="M14.096 29.312C14.288 30.624 14.416 31.232 14.704 31.968C15.312 33.536 16.24 34.752 17.52 35.616C19.056 36.64 21.616 37.28 24.24 37.28C27.664 37.28 30.192 36.48 31.888 34.848C33.2 33.536 33.904 31.68 33.904 29.408C33.904 27.008 33.072 25.248 31.248 23.904C30.096 23.104 28.56 22.496 26.032 21.952C22.48 21.152 22.224 21.088 21.52 20.8C20.048 20.16 19.344 19.232 19.344 17.888C19.344 15.808 21.232 14.4 24.016 14.4C25.776 14.4 27.184 14.944 28.016 15.936C28.56 16.576 28.784 17.12 28.976 18.208L33.552 17.664C33.168 15.968 32.784 15.008 31.984 13.952C30.416 11.84 27.664 10.72 24.016 10.72C18.512 10.72 14.928 13.6 14.928 18.048C14.928 20.192 15.76 21.952 17.392 23.232C18.544 24.128 19.824 24.64 22.224 25.184C26.64 26.208 26.832 26.24 27.792 26.752C28.784 27.296 29.392 28.384 29.392 29.696C29.392 32.096 27.472 33.536 24.272 33.536C22.192 33.536 20.528 32.864 19.6 31.648C19.024 30.912 18.832 30.272 18.704 28.832L14.096 29.312Z" fill="${fontColor}" fill-rule="nonzero" opacity="1" stroke-width="0"/>
  </g>
  </svg>`
}

export type DraftItem = {
  type: ObservatoryType
  id: string
  name: string
}

export const ObservatoryLayer = ({ showControl = true }: { showControl?: boolean }) => {
  const state = useGlobalState()
  const [targetTypes, setTargetTypes] = React.useState<ObservatoryType[]>(state.app.observatoryTypes)
  const [draftDialog, setDraftDialog] = React.useState<boolean>(false)
  const [draftItems, setDraftItems] = React.useState<DraftItem[]>([])
  const isStageForecast = isPrivateMode // 予測水位でステータスを判定する

  const sourceId = LAYERS.OBSERVATORY.id
  const layerId = LAYERS.OBSERVATORY.id

  const updateStageForecastStatus = () => {
    if (state.observatories.length === 0) return
    state.observatory.stageForecastData.forEach(data => {
      const stage = data.stage
      let maxLevel = stage.level
      if (isStageForecast === true && data.timeSeries.length > 0) {
        const forecasts = data.timeSeries.filter(t => t.type === 'forecast' && t.level !== undefined).map(t => t.level) as number[]
        if (forecasts.length === 0) return
        if (stage.level !== undefined) {
          forecasts.push(stage.level)
        }
        maxLevel = Math.max(...forecasts)
      }
      stage.status = utils.getStageStatus(stage, maxLevel)
    })
    state.map.updateSourceData(sourceId,
      {
        type: 'FeatureCollection',
        features: getGeojsonFeatures()
      })
  }

  React.useEffect(() => {
    if (!state.mapbox) return
    (async() => {
      await load()
    })()
    state.mapbox?.on('mouseenter', layerId, mouseEnterListener)
    state.mapbox?.on('mouseleave', layerId, mouseLeaveListener)
    state.mapbox?.on('mousemove', layerId, mousemoveListener)
    return () => {
      state.mapbox?.off('mouseenter', layerId, mouseEnterListener)
      state.mapbox?.off('mouseleave', layerId, mouseLeaveListener)
      state.mapbox?.off('mousemove', layerId, mousemoveListener)
      state.map.setLayoutVisibility(layerId, false)
    }
  }, [state.mapbox])

  React.useEffect(() => {
    state.observatory.updateStatus(state.app.baseDate)
  }, [state.app.baseDate])

  React.useEffect(() => {
    updateStageForecastStatus()
  }, [state.observatory.stageForecastData])

  React.useEffect(() => {
    state.map.updateSourceData(sourceId,
      {
        type: 'FeatureCollection',
        features: getGeojsonFeatures()
      })
    state.mapbox?.on('click', layerId, clickListener)
    return () => {
      state.mapbox?.off('click', layerId, clickListener)
    }
  }, [state.observatories, targetTypes])

  React.useEffect(() => {
    state.app.setObservatoryTypes(targetTypes)
  }, [targetTypes])

  const getGeojsonFeatures = (): Array<GeoJSON.Feature> => {
    const targets = state.observatories.filter(obs => {
      return targetTypes.includes(obs.type)
    })

    // 並び順考慮 後の方が全面に表示される
    const stages = targets.filter(obs => obs.type === ObservatoryType.Stage && !ForecastStages.includes(obs.id))
    const stagesEx = targets.filter(obs => obs.type === ObservatoryType.Stage && ForecastStages.includes(obs.id))
    const etcs = targets.filter(obs => obs.type !== ObservatoryType.Stage)
    const sortedTargets = [...etcs, ...stages, ...stagesEx]

    return sortedTargets.map(obs => {
      let icon = ''
      if (obs.type === ObservatoryType.Rain) {
        icon = `${IDs.observatory.rainImage}_${obs.status}`
      } else if (obs.type === ObservatoryType.Stage) {
        if (isPrivateMode && ForecastStages.includes(obs.id)) {
          icon = `${IDs.observatory.stageImage}_${obs.status}_ex`
        } else {
          icon = `${IDs.observatory.stageImage}_${obs.status}`
        }
      } else if (obs.type === ObservatoryType.Camera) {
        icon = IDs.observatory.cameraImage
      } else if (obs.type === ObservatoryType.Tide) {
        icon = IDs.observatory.tideImage
      } else if (obs.type === ObservatoryType.Pump) {
        if (isPrivateMode && ForecastPumps.includes(obs.id)) {
          icon = `${IDs.observatory.pumpImage}_${obs.status}_ex`
        } else {
          icon = `${IDs.observatory.pumpImage}_${obs.status}`
        }
      } else if (obs.type === ObservatoryType.Suijin) {
        icon = `${IDs.observatory.suijinImage}_${obs.status}`
      }
      return {
        type: 'Feature',
        properties: {
          type: obs.type,
          id: obs.id,
          name: obs.name,
          status: obs.status,
          icon: icon
        },
        geometry: {
          type: 'Point',
          coordinates: [obs.lng, obs.lat]
        }
      }
    })
  }

  const load = async () => {

    // rain images
    Object.values(RainStatus).forEach(r => {
      const imageId = `${IDs.observatory.rainImage}_${r.key}`
      state.map.addSvg(imageId, 48, getRainSvg(r.color))
    })

    // stage images
    Object.values(StageAlertLevel).forEach(s => {
      const imageId = `${IDs.observatory.stageImage}_${s.key}`
      state.map.addSvg(imageId, 48, getStageSvg(s.color))
    })

    // stage ex images
    Object.values(StageAlertLevel).forEach(s => {
      const imageId = `${IDs.observatory.stageImage}_${s.key}_ex`
      state.map.addSvg(imageId, 48, getStageExSvg(s.color))
    })

    // camera image
    state.map.addSvg(IDs.observatory.cameraImage, 48, getCameraSvg('#9F0039'))

    // tide image
    state.map.addSvg(IDs.observatory.tideImage, 48, getTideSvg('#2D82ED'))

    // pump images
    Object.values(PumpAlertLevel).forEach(r => {
      const imageId = `${IDs.observatory.pumpImage}_${r.key}`
      state.map.addSvg(imageId, 48, getPumpSvg(r.color))
    })

    // pump ex images
    Object.values(PumpAlertLevel).forEach(r => {
      const imageId = `${IDs.observatory.pumpImage}_${r.key}_ex`
      state.map.addSvg(imageId, 48, getPumpExSvg(r.color))
    })

    Object.values(SuijinStatus).forEach(r => {
      const imageId = `${IDs.observatory.suijinImage}_${r.key}`
      state.map.addSvg(imageId, 48, getSuijinSvg(r.color))
    })

    state.map.updateSourceData(sourceId, {
      type: 'FeatureCollection',
      features: getGeojsonFeatures()
    })
    state.map.setLayoutVisibility(layerId, true)

    state.app.setLoading(true)
    try  {
      await state.observatory.fetch()
    } catch (e) {
      console.error('ObservatoryLayer load error:', e)
      state.app.setSnackbarMessage('読み込みに失敗しました')
    } finally {
      state.app.setLoading(false)
    }
  }

  const popup = new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false
  })

  const mouseEnterListener = () => {
    if (!state.mapbox) return
    state.mapbox.getCanvas().style.cursor = 'pointer'
  }

  const mousemoveListener = (e: any) => {
    if (!state.mapbox) return
    if (!e.features || e.features.length === 0) return

    const feature = e.features[0]
    const obsName = feature.properties.name
    const typeName = ObservatoryTypeName[feature.properties.type as ObservatoryType]
    const html = `${typeName}: ${obsName}`

    if (popup.isOpen()) {
      popup.setHTML(html)
      popup.setLngLat(e.lngLat)
    } else {
      popup
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(state.mapbox)
    }
  }

  const mouseLeaveListener = () => {
    if (!state.mapbox) return
    state.mapbox.getCanvas().style.cursor = ''
    if (popup.isOpen()) {
      popup.remove()
    }
  }

  const clickListener = (e: any) => {
    const targets =  e.features
      .filter((f: any) => f.layer.id === LAYERS.OBSERVATORY.id)
      .map((f: any) => {
        const target = f.properties
        return {
          type: target.type,
          id: target.id,
          name: target.name
        }
      })
    if (targets.length === 1) {
      showObservatoryContainer(targets[0])
    } else if (targets.length > 1) {
      // typeでソート
      targets.sort((a: any, b: any) => {
        if (a.type > b.type) return 1
        if (a.type < b.type) return -1
        return 0
      })
      setDraftItems(targets)
      setDraftDialog(true)
    }
  }

  const showObservatoryContainer = (target: DraftItem) => {
    const row = state.observatory.getObservatory(target.type, target.id)
    if (row !== null) {
      state.observatory.setSelected(row)
      state.app.setObservatoryContainer(true)
    }
    setDraftDialog(false)
  }

  const changeTargetType = (type: ObservatoryType) => {
    const arr = [...targetTypes]
    const idx = arr.indexOf(type)
    if (idx >= 0) {
      arr.splice(idx, 1)
    } else {
      arr.push(type)
    }
    setTargetTypes(arr)
    localStorage.setItem('observatoryTypes', JSON.stringify(arr))
  }

  const ObservatoryTypeStack = ({type, label, children}: React.PropsWithChildren< {type: ObservatoryType, label: string } >) => {
    return (
      <Stack direction={'row'} alignItems={'center'}>
        <FormControlLabel
          sx={{width: 100}}
          control={<Checkbox size='small' sx={{maxHeight: 30}} />}
          label={<Typography variant='body2'>{label}</Typography>}
          checked={targetTypes.includes(type)}
          onChange={() => changeTargetType(type)}
        />
        {children}
      </Stack>
    )
  }

  const PumpIcon = () => {
    return <div
      dangerouslySetInnerHTML={{ __html: getPumpSvg('#ffffff') }}
      style={{ width: '20px', height: '20px' }}
    />
  }

  return (
    <>
      {showControl && (
        <Stack direction={'column'} component={Paper} pl={1} py={'5px'} width={160}>
          <ObservatoryTypeStack type={ObservatoryType.Suijin} label={'浸水ｾﾝｻ'}>
            <img src={SuijinPng} height={20} width={20}></img>
          </ObservatoryTypeStack>
          <ObservatoryTypeStack type={ObservatoryType.Stage} label={'水位'}>
            <ChangeHistoryIcon fontSize='small' />
          </ObservatoryTypeStack>
          { isPrivateMode && (
            <Stack direction={'row'} alignItems={'center'}>
              <Typography variant='body2' ml={'20px'}>{'('}</Typography>
              <Typography variant='body2' ml={'5px'} mr={'8px'}>{'予測地点'}</Typography>
              <ChangeHistoryIcon fontSize='small' sx={{color: '#ff9800'}} />
              <Typography variant='body2' ml={'5px'}>{')'}</Typography>
            </Stack>
          )
          }
          <ObservatoryTypeStack type={ObservatoryType.Pump} label={'ポンプ'}>
            <PumpIcon />
          </ObservatoryTypeStack>
          <ObservatoryTypeStack type={ObservatoryType.Rain} label={'雨量'}>
            <RadioButtonUncheckedIcon fontSize='small' />
          </ObservatoryTypeStack>
          <ObservatoryTypeStack type={ObservatoryType.Camera} label={'カメラ'}>
            <CameraAltIcon fontSize='small' />
          </ObservatoryTypeStack>
          <ObservatoryTypeStack type={ObservatoryType.Tide} label={'潮位'}>
            <HexagonIcon fontSize='small' />
          </ObservatoryTypeStack>
        </Stack>
      )}
      <Dialog
        open={draftDialog}
        onClose={() => {setDraftDialog(false)}}
      >
        <DialogContent dividers sx={{pl: 0, pr: 0, py: 1}}>
          <List dense disablePadding>
            {
              draftItems.map((item, i) => {
                return (
                  <ListItem disablePadding key={i}>
                    <ListItemButton onClick={() => showObservatoryContainer(item)} sx={{pr: 4}}>
                      <ListItemIcon>
                        { item.type === ObservatoryType.Stage && <ChangeHistoryIcon fontSize='small' /> }
                        { item.type === ObservatoryType.Rain && <RadioButtonUncheckedIcon fontSize='small' /> }
                        { item.type === ObservatoryType.Camera && <CameraAltIcon fontSize='small' /> }
                        { item.type === ObservatoryType.Tide && <HexagonIcon fontSize='small' /> }
                        { item.type === ObservatoryType.Pump && <PumpIcon /> }
                        { item.type === ObservatoryType.Suijin && <img src={SuijinPng} height={20} width={20}></img> }
                      </ListItemIcon>
                      <ListItemText primary={item.name} />
                    </ListItemButton>
                  </ListItem>
                )
              })
            }
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDraftDialog(false)} size='small'>
            キャンセル
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
